/**
 * Settings — ChatGPT-style centered modal with a left side nav.
 *
 * SELF-CONTAINED: open state and active section both live in local /
 * parent React state, not the URL. The underlying app routes (table
 * view, etc.) stay mounted and unchanged while Settings is open;
 * closing the modal returns the user to wherever they were without
 * any history navigation.
 *
 * Each section is a separate panel mounted in the right-hand pane.
 */
import { useState } from "react";
import { Modal } from "@mantine/core";
import {
  IconDatabase,
  IconLock,
  IconShield,
  IconUser,
  IconX,
} from "@tabler/icons-react";

import type { ApiClient } from "../api/client";
import type { WhoAmI } from "../api/types";
import { DatabasesPanel } from "./settings/DatabasesPanel";
import { AccessControlPanel } from "./settings/AccessControlPanel";
import { SecurityPanel } from "./settings/SecurityPanel";
import { AccountPanel } from "./settings/AccountPanel";

type SectionKey = "database" | "access" | "security" | "account";

const SECTIONS: {
  key: SectionKey;
  label: string;
  icon: typeof IconDatabase;
  adminOnly: boolean;
}[] = [
  { key: "database", label: "Database",       icon: IconDatabase, adminOnly: true  },
  { key: "access",   label: "Access control", icon: IconLock,     adminOnly: true  },
  { key: "security", label: "Security",       icon: IconShield,   adminOnly: true  },
  { key: "account",  label: "Account",        icon: IconUser,     adminOnly: false },
];

export function SettingsModal({
  opened,
  onClose,
  api,
  isAdmin,
  user,
}: {
  opened: boolean;
  onClose: () => void;
  api: ApiClient;
  isAdmin: boolean;
  user: WhoAmI | undefined;
}) {
  const visible = SECTIONS.filter((s) => isAdmin || !s.adminOnly);
  const [section, setSection] = useState<SectionKey>(visible[0]?.key ?? "account");
  const active = visible.find((s) => s.key === section) ?? visible[0];

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      withCloseButton={false}
      size="1080px"
      centered
      padding={0}
      radius="md"
      // Click-outside is intentionally disabled; the X button in the
      // sidebar is the explicit close. ESC still closes (Mantine's
      // closeOnEscape defaults true) for power users.
      closeOnClickOutside={false}
      overlayProps={{ blur: 3, backgroundOpacity: 0.35 }}
      classNames={{ body: "kse-settings-modal__body" }}
    >
      <div className="kse-settings-modal">
        <aside className="kse-settings-modal__sidebar">
          <button
            type="button"
            className="kse-settings-modal__close"
            onClick={onClose}
            aria-label="Close settings"
          >
            <IconX size={18} />
          </button>
          <nav className="kse-settings-modal__nav">
            {visible.map((s) => {
              const Icon = s.icon;
              const isActive = s.key === active?.key;
              return (
                <button
                  key={s.key}
                  type="button"
                  className="kse-settings-modal__navitem"
                  data-active={isActive}
                  onClick={() => setSection(s.key)}
                >
                  <Icon size={16} stroke={1.6} />
                  <span>{s.label}</span>
                </button>
              );
            })}
          </nav>
        </aside>

        <section className="kse-settings-modal__content">
          <header className="kse-settings-modal__header">
            <h2 className="kse-settings-modal__title">{active?.label}</h2>
          </header>
          <div className="kse-settings-modal__panel">
            {active?.key === "database" && <DatabasesPanel api={api} />}
            {active?.key === "access"   && <AccessControlPanel api={api} currentUserOid={user?.oid} />}
            {active?.key === "security" && <SecurityPanel />}
            {active?.key === "account"  && <AccountPanel user={user} />}
          </div>
        </section>
      </div>
    </Modal>
  );
}
